import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import useAntibodyMapState from 'antibodies/antibody-map/useAntibodyMapState';
import {INTERACTIVE_WRAPPER_DATA_ATTR} from 'antibodies/visualization/InteractiveComponentWrapper';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import useTransientCurrentPolymerModel from 'molbio/hooks/useTransientCurrentPolymerModel';
import ProteinMapContainer from 'molbio/maps/standalone/AaSequenceMapContainer';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ClickOutWrapper from 'ui-library/ClickOutWrapper';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export interface ComponentPreviewProps {
  componentLabel: string;
  antibodyId: string;
  mapStateKey: string;
  selectedProteinId: string | null;
  toggleExpandPreview: () => void;
  closePreview: () => void;
}

// Extracted sub-component to avoid calling useTransientCurrentPolymerModel conditionally/with empty id.
function ProteinMapContents({selectedProteinId}: {selectedProteinId: string}): JSX.Element {
  const aaSequence = useTransientCurrentPolymerModel(selectedProteinId) as AaSequence | null;
  return (
    <>
      {aaSequence == null && <CenteredHorizontalThrobber />}
      {aaSequence != null && <ProteinMapContainer protein={aaSequence} />}
    </>
  );
}

export default function ComponentPreview({
  componentLabel,
  antibodyId,
  mapStateKey,
  selectedProteinId,
  toggleExpandPreview,
  closePreview,
}: ComponentPreviewProps): JSX.Element {
  const {
    state: {expandPreview, showMinimap},
    setShowMinimap,
  } = useAntibodyMapState(mapStateKey);

  function onClickOut(e: MouseEvent): void {
    // Do not close the preview if any floating menus, modals, etc are open from the preview
    // or if the user is interacting with an interactive component wrapper (i.e. a domain in the antibody map
    // or a chain in the antibody chain legend). Clicking on the interactive component will already close the preview.
    const clickedInteractiveComponent =
      $(e.target as Element).parents(`[${INTERACTIVE_WRAPPER_DATA_ATTR}]`).length > 0;
    if (
      $('.modal-open').length === 0 &&
      $('#context-menu:visible').length === 0 &&
      !$(e.target as Element).hasClass('backdrop') &&
      $(e.target as Element).parents('.absoluteDropdownContainer').length === 0 &&
      !clickedInteractiveComponent
    ) {
      closePreview();
    }
  }

  return (
    <ClickOutWrapper onClickOut={onClickOut}>
      <div
        className={css(styles.wrapper)}
        {...getTestProps({component: 'ComponentPreview', element: 'root', key: componentLabel})}
        data-amp-safe={false}
      >
        <div className={css(styles.header)}>
          <div>
            <span className={css(styles.componentLabel)}>{componentLabel}</span>
            {selectedProteinId != null && (
              <>
                <span className={css(styles.componentLabel)}>: </span>
                <BenchChip id={selectedProteinId} size={ChipSize.SMALL} />
              </>
            )}
          </div>
          <div className={css(styles.buttonsWrapper)}>
            {expandPreview && (
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                className={showMinimap ? css(styles.activeMinimap) : ''}
                icon="antibody"
                iconAltText={showMinimap ? 'Hide minimap' : 'Show minimap'}
                onClick={() => {
                  setShowMinimap(!showMinimap);
                }}
                size={ButtonSize.SMALL}
              />
            )}
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon={expandPreview ? 'collapse-right-full' : 'expand-right-full'}
              iconAltText={expandPreview ? 'Collapse component preview' : 'Expand component preview'}
              onClick={() => {
                trackEvent(
                  expandPreview
                    ? ANTIBODIES_ANALYTICS_ACTIONS.COLLAPSED_COMPONENT_PREVIEW
                    : ANTIBODIES_ANALYTICS_ACTIONS.EXPANDED_COMPONENT_PREVIEW,
                  {antibodyId, componentLabel}
                );
                toggleExpandPreview();
              }}
              size={ButtonSize.SMALL}
            />
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon="cross-thick"
              iconAltText="Close component preview"
              onClick={closePreview}
              size={ButtonSize.SMALL}
            />
          </div>
        </div>
        <div className={css(expandPreview ? styles.proteinMapWrapperExpanded : styles.proteinMapWrapper)}>
          {selectedProteinId != null ? (
            <ProteinMapContents selectedProteinId={selectedProteinId} />
          ) : (
            <div className={css(styles.emptyState)}>No sequence associated.</div>
          )}
        </div>
      </div>
    </ClickOutWrapper>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    border: `1px solid ${Color.GRAY_100}`,
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    backgroundColor: Color.GRAY_100,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    borderRadiusTopLeft: '4px',
    borderRadiusTopRight: '4px',
    flex: '0 0 44px',
  },
  buttonsWrapper: {
    display: 'flex',
    gap: '8px',
  },
  componentLabel: {
    fontWeight: 600,
  },
  activeMinimap: {
    color: Color.BLUE_400,
  },
  icon: {
    cursor: 'pointer',
    width: '16px',
    height: '16px',
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: Color.GRAY_600,
  },
  proteinMapWrapper: {
    flex: '0 0 236px',
  },
  proteinMapWrapperExpanded: {
    flex: '1 0 auto',
  },
});
